import DoneIcon from '@mui/icons-material/Done';
import { Button, Chip, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { backend } from '../axios/backend';
import { useAppContext } from '../context/AppContext';
import { Tag } from '../types/tag';
import { parseErrorMessage } from '../utils/error-message';
import InputFormik from './formik/InputFormik';

type UpsertTagModalProps = {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  toEdit?: Tag;
  tags: Tag[];
};

const possibleColors: { backgroundColor: string; color: string; name: string }[] = [
  { backgroundColor: '#00ffff', color: '#000', name: 'Blau' },
  { backgroundColor: '#ff0000', color: '#fff', name: 'Rot' },
  { backgroundColor: '#008800', color: '#fff', name: 'Grün' },
  { backgroundColor: '#ffff00', color: '#000', name: 'Gelb' },
];

const UpsertTagModal = (props: UpsertTagModalProps) => {
  const { alertError, alertSuccess } = useAppContext();

  const queryClient = useQueryClient();
  const addMutation = useMutation(
    (options: { name: string; color: string; backgroundColor: string }) => backend.post('tag', options),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tags');
        props.setOpen(false);
        alertSuccess('Tag hinzugefügt.');
      },
      onError: (e) => {
        alertError(parseErrorMessage(e));
      },
    },
  );

  const TagSchema = useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .min(1, 'Bitte gib einen Namen an.')
        .notOneOf(
          props.tags.map((t) => t.name),
          'Bitte wähle einen nicht bereits verwendeten Namen.',
        )
        .required('Bitte gib einen Namen an.'),
      color: Yup.string()
        .matches(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i, 'Bitte wähle eine gültige Farbe aus.')
        .required('Bitte wähle eine Farbe aus.'),
      backgroundColor: Yup.string()
        .matches(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i, 'Bitte wähle eine gültige Farbe aus.')
        .required('Bitte wähle eine Farbe aus.'),
    });
  }, [props.tags]);

  return (
    <Dialog open={props.isOpen} onClose={() => props.setOpen(false)} fullWidth>
      <DialogTitle>Tag {props.toEdit ? 'bearbeiten' : 'hinzufügen'}</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            name: props.toEdit?.name || '',
            color: props.toEdit?.color || '#000',
            backgroundColor: props.toEdit?.backgroundColor || '#00ffff',
          }}
          onSubmit={(values) => {
            if (props.toEdit) {
              // To-Do
            } else {
              console.log(values);
              addMutation.mutate(values);
            }
          }}
          validationSchema={TagSchema}
        >
          {({ handleSubmit, setFieldValue, values }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={1.5}>
                <InputFormik name="name" placeholder="Name" />
                <Stack direction="row" spacing={0.5}>
                  {possibleColors.map((possibleColor) => (
                    <Chip
                      label={possibleColor.name}
                      style={{ backgroundColor: possibleColor.backgroundColor, color: possibleColor.color }}
                      onClick={() => {
                        setFieldValue('color', possibleColor.color);
                        setFieldValue('backgroundColor', possibleColor.backgroundColor);
                      }}
                      icon={
                        values.backgroundColor === possibleColor.backgroundColor ? (
                          <DoneIcon style={{ color: possibleColor.color }} />
                        ) : undefined
                      }
                    />
                  ))}
                </Stack>
                <Button type="submit">Hinzufügen</Button>
              </Stack>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default UpsertTagModal;
